// URL-hash codec for the reader's place. The hash holds the parasha ref and,
// optionally, the open-source PATH (the chain you drilled into) so a refresh or a
// shared link reopens you exactly where you were, not just at the parasha - the
// most literal "mareh makom". Segments are "~"-joined and individually
// percent-encoded; refs never contain "~". A bare "#<ref>" (no "~") is the
// parasha-only form and stays backward compatible.

export interface RouteState {
  ref: string; // parasha ref, e.g. "Numbers 16:1-18:32"
  path: string[]; // [verseRef, sourceRef, citedRef, ...]; empty when nothing is open
}

// Decode a location.hash string, tolerating a malformed fragment (a hand-edited
// URL with a bad percent-sequence would otherwise throw URIError).
export function parseRoute(hash: string): RouteState {
  const raw = hash.replace(/^#/, "");
  if (!raw) return { ref: "", path: [] };
  let segs: string[];
  try {
    segs = raw.split("~").map((s) => decodeURIComponent(s).trim());
  } catch {
    return { ref: "", path: [] };
  }
  const [ref, ...path] = segs;
  return { ref: ref ?? "", path: path.filter((s) => s.length > 0) };
}

// Build a "#..." hash from a parasha ref and an optional open-source path.
export function buildRoute(ref: string, path: string[] = []): string {
  if (!ref) return "";
  return "#" + [ref, ...path].map(encodeURIComponent).join("~");
}

// Does a parsed route's ref name the SAME parasha the reader is currently showing?
// An empty ref means "this week" - it matches the parasha the hashless cold-load
// resolved to (its navParashaRef). The pure core of app.ts's warm-vs-cold traversal
// decision; a future doubled-week aliasing change belongs here.
export function routeMatchesParasha(ref: string, currentViewRef: string, navParashaRef: string): boolean {
  return ref ? ref === currentViewRef : navParashaRef === currentViewRef;
}
